import React from "react";
import { AHIcon, AHText, Box, ListItem, SubTodoIndicator } from "arkh";
import { graphql, useFragment } from "react-relay";

import { WorkspaceTodoStoresItemContainer } from "./WorkspaceTodoStoresItemContainer";
import { WorkspaceTodoStoresListItem_todo$key } from "./__generated__/WorkspaceTodoStoresListItem_todo.graphql";
import { WorkspaceTodoStoresListItemResponse_todoResponse$key } from "./__generated__/WorkspaceTodoStoresListItemResponse_todoResponse.graphql";
import { AHImageStackNew } from "../../../RoutineOccasion/TaskExecution/AHImageStack";

import { WorkspaceTodoStoresListItemResponseImage_todoImageResponse$key } from "./__generated__/WorkspaceTodoStoresListItemResponseImage_todoImageResponse.graphql";
import { TouchableOpacity } from "react-native";
import { TodoCircleNew } from "../../../TodoCategory/TodoCircleNew";
import { FormattedDateInWorkspace } from "../../../../common/dates/FormattedDate";
import { OrgTableTitle } from "../../OrgTable/OrgTable";
import { useLightboxControls } from "@/src/common/lightbox/LightboxProvider";

export function WorkspaceTodoStoresListItem(props: {
  todo: WorkspaceTodoStoresListItem_todo$key;
  isLast: boolean;
  onPress(todoId: string, storeName: string, timezone: string): void;
  disabled?: boolean;
}) {
  const data = useFragment(
    graphql`
      fragment WorkspaceTodoStoresListItem_todo on Todo {
        store {
          id
          name
          timezone
        }
        status
        id
        subTodos {
          status
        }
        response {
          ...WorkspaceTodoStoresListItemResponse_todoResponse
        }
        resolvedAtDate {
          ...FormattedDate
        }
        commentCount
      }
    `,
    props.todo
  );

  return (
    <ListItem
      disabled={props.disabled}
      isLast={props.isLast}
      onPress={() => {
        props.onPress(data.id, data.store?.name ?? "", data.store!.timezone);
      }}
      border="border-primary"
      displayMode="TABLE"
    >
      <WorkspaceTodoStoresItemContainer
        storeName={
          <Box style={{ flexDirection: "row", alignItems: "center" }}>
            <AHIcon color="text-washed" type="store" spaceRight="small" />
            <OrgTableTitle>{data.store?.name ?? ""}</OrgTableTitle>
          </Box>
        }
        completedAt={
          <Box style={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
            <AHText>
              {data.resolvedAtDate ? (
                <FormattedDateInWorkspace date={data.resolvedAtDate} />
              ) : null}
            </AHText>
          </Box>
        }
        comments={
          <Box style={{ flexDirection: "row", alignItems: "center" }}>
            <AHText>{data.commentCount > 0 ? data.commentCount : null}</AHText>
          </Box>
        }
        status={
          <Box
            style={{
              flexDirection: "row",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            {(data.subTodos?.length ?? 0) > 0 ? (
              <SubTodoIndicator
                statuses={data.subTodos?.map((subTodo) => subTodo.status) ?? []}
              />
            ) : null}
            {data.status === "DONE" ? (
              <Box spaceLeft="medium">
                <TodoCircleNew
                  status="DONE"
                  expired={false}
                  size="smallMedium"
                />
              </Box>
            ) : null}
            {props.disabled ? null : (
              <AHIcon type="chevron-right" color="darkText" spaceLeft="small" />
            )}
          </Box>
        }
        response={
          data.response ? (
            <WorkspaceTodoStoresListItemResponse response={data.response} />
          ) : null
        }
      />
    </ListItem>
  );
}

function WorkspaceTodoStoresListItemResponse(props: {
  response: WorkspaceTodoStoresListItemResponse_todoResponse$key;
}) {
  const response = useFragment(
    graphql`
      fragment WorkspaceTodoStoresListItemResponse_todoResponse on TodoResponse {
        ... on TodoResponseNumber {
          value
          __typename
        }
        ... on TodoResponseImage {
          ...WorkspaceTodoStoresListItemResponseImage_todoImageResponse
          __typename
        }
        ... on TodoResponseText {
          text
          __typename
        }
      }
    `,
    props.response
  );

  switch (response.__typename) {
    case "TodoResponseNumber":
      return (
        <Box style={{ flexDirection: "row", alignItems: "center" }}>
          <AHText>{response.value}</AHText>
        </Box>
      );
    case "TodoResponseText":
      return (
        <Box style={{ flexDirection: "row", alignItems: "center" }}>
          <AHText>{response.text}</AHText>
        </Box>
      );
    case "TodoResponseImage":
      return (
        <WorkspaceTodoStoresListItemResponseImage imageResponse={response} />
      );
  }

  return null;
}

function WorkspaceTodoStoresListItemResponseImage(props: {
  imageResponse: WorkspaceTodoStoresListItemResponseImage_todoImageResponse$key;
}) {
  const response = useFragment(
    graphql`
      fragment WorkspaceTodoStoresListItemResponseImage_todoImageResponse on TodoResponseImage {
        images {
          id
          sizes {
            height
            width
          }
          ...Image_image
          ...AHImageStackComponent_Images
        }
        __typename
      }
    `,
    props.imageResponse
  );

  const lightbox = useLightboxControls();

  if (response.images.length === 0) return null;

  return (
    <>
      <TouchableOpacity
        onPress={() => {
          lightbox.openLightbox({
            index: 0,
            images: response.images.map((image) => ({
              image: {
                type: "remote",
                image: image,
              },
              mediaType: "image" as const,
              dimensions: {
                width: image.sizes?.[0]?.width ?? 1,
                height: image.sizes?.[0]?.height ?? 1,
              },
            })),
          });
        }}
      >
        <Box style={{ flexDirection: "row", alignItems: "center" }}>
          <AHImageStackNew images={response.images} />
        </Box>
      </TouchableOpacity>
    </>
  );
}
